import { useState, type ReactNode } from 'react';

import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  MenuItem,
  MenuList,
  Stack,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import Body from '@/components/layout/Body';
import Sidebar from '@/components/layout/Sidebar';
import {
  CAM_COMPACT,
  CAM_REGULAR,
  camContainerSx,
} from '@/cam-admin/responsive';

export type CamNavItem = { label: string; icon: string; path?: string };

// The CAM admin's top-level navigation, mirroring the product areas in the
// figma rail. Only Care circles has a page today; the rest are placeholders,
// so their `path` is absent and clicking them is a no-op.
export const CAM_NAV: CamNavItem[] = [
  { label: 'Care circles', icon: 'house', path: '/care-circles' },
  { label: 'Accounts', icon: 'calendar' },
  { label: 'Referring partners', icon: 'heart' },
  { label: 'Organisations', icon: 'magnifying-glass' },
  { label: 'Products', icon: 'users' },
];

/**
 * The CAM admin chrome: the KareHero app bar and the product-area nav rail
 * (with a compact hamburger drawer), shared by every admin page — the care
 * circles index and the per-circle control panel — so the shell is defined
 * once and can't drift between them. Presentational: routing is injected via
 * `onNavigate`, so the shell (and the pages that use it) stay router-agnostic
 * and render straight into Storybook without a router.
 *
 * `children` is the content area beside the rail (a Layout). `overlays` renders
 * absolutely-positioned siblings over the whole shell (a page's notifications
 * view, a snackbar).
 */
const CamShell = ({
  activeNav = 'Care circles',
  nav = CAM_NAV,
  onNavigate,
  bellBadge,
  onBell,
  children,
  overlays,
}: {
  activeNav?: string;
  // The nav rail items. Defaults to the standard CAM rail; a prototype can pass
  // its own (e.g. the descoping build adds a Caseloads item).
  nav?: CamNavItem[];
  onNavigate?: (path: string) => void;
  bellBadge?: number;
  onBell?: () => void;
  children: ReactNode;
  overlays?: ReactNode;
}) => {
  const theme = useTheme();
  // Compact-only: the nav rail collapses to a hamburger that opens this drawer.
  const [navOpen, setNavOpen] = useState(false);

  // The section nav, shared by the persistent rail and the compact drawer.
  const navMenu = (
    <MenuList>
      {nav.map((item) => {
        const active = item.label === activeNav;
        return (
          <MenuItem
            key={item.label}
            aria-current={active ? 'page' : undefined}
            onClick={() => {
              setNavOpen(false);
              if (item.path) onNavigate?.(item.path);
            }}
          >
            <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
              <Icon
                icon={item.icon as never}
                fontSize={18}
                color={
                  active
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary
                }
              />
              <Typography
                variant='body2'
                color={active ? 'primary.main' : 'text.secondary'}
              >
                {item.label}
              </Typography>
            </Stack>
          </MenuItem>
        );
      })}
    </MenuList>
  );

  return (
    <Box
      sx={[
        {
          height: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'background.default',
        },
        camContainerSx,
      ]}
    >
      <AppBar position='static' color='transparent' elevation={0}>
        <Toolbar>
          {/* hamburger: compact only, opens the nav drawer */}
          <IconButton
            aria-label='Open navigation'
            size='small'
            onClick={() => setNavOpen(true)}
            sx={{
              mr: 1,
              display: 'none',
              [CAM_COMPACT]: { display: 'inline-flex' },
            }}
          >
            <Icon icon={'bars' as never} fontSize={18} />
          </IconButton>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '6px',
              bgcolor: 'primary.main',
              mr: 1,
            }}
          />
          <Typography variant='h6' color='primary.main'>
            KareHero
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
            <IconButton
              aria-label='Notifications'
              size='small'
              onClick={onBell}
            >
              <Badge badgeContent={bellBadge} color='error' overlap='circular'>
                <Icon icon={'bell' as never} fontSize={18} />
              </Badge>
            </IconButton>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'custom.neutral.300',
                color: 'common.white',
                fontSize: 13,
              }}
            >
              OP
            </Avatar>
          </Stack>
        </Toolbar>
        <Divider />
      </AppBar>

      <Body>
        {/* persistent rail — hidden in compact, where the drawer takes over */}
        <Sidebar sx={{ [CAM_COMPACT]: { display: 'none' } }}>{navMenu}</Sidebar>
        {children}
      </Body>

      {/* compact nav drawer: an in-tree overlay (not a portal Drawer, which
          would escape the scaled laptop frame). Reachable only via the
          hamburger, which is compact-only; the regular query forces it hidden
          if the stage is resized back up to desktop while open. */}
      <Box
        aria-hidden={!navOpen}
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 10,
          pointerEvents: navOpen ? 'auto' : 'none',
          [CAM_REGULAR]: { display: 'none' },
        }}
      >
        {/* backdrop */}
        <Box
          onClick={() => setNavOpen(false)}
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'custom.charcoal',
            opacity: navOpen ? 0.4 : 0,
            transition: 'opacity 200ms ease',
          }}
        />
        {/* sliding panel — reuses the rail's surface */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            transform: navOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 220ms ease',
          }}
        >
          <Sidebar sx={{ height: '100%' }}>{navMenu}</Sidebar>
        </Box>
      </Box>

      {overlays}
    </Box>
  );
};

export default CamShell;
